import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { EventsAPI } from '../api';
import { UserContext } from '../context/UserContext';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { EVENT_TYPES } from '../utils/eventTypes';
import { EVENT_TYPE_SCHEMAS } from '../utils/eventTypeSchemas';

export default function EventEdit() {
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [ev, setEv] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState(null);

  const canManage = ev && (Array.isArray(user?.roles) && user.roles.includes('admin') || user?.id === ev.ownerId);
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');

  useEffect(() => {
    (async () => {
      try {
        const e = await EventsAPI.get(id);
        setEv(e);
        const isCustomType = e.eventType && !EVENT_TYPES.includes(e.eventType);
  const existingUserExtras = Array.isArray(e.customFields) ? e.customFields : [];
        // Pad to 5 rows for UI
        const paddedUserExtras = [
          existingUserExtras[0] || { label:'', value:'' },
          existingUserExtras[1] || { label:'', value:'' },
          existingUserExtras[2] || { label:'', value:'' },
          existingUserExtras[3] || { label:'', value:'' },
          existingUserExtras[4] || { label:'', value:'' },
        ];
        setForm({
          title: e.title || '',
          description: e.description || '',
          tags: Array.isArray(e.tags) ? e.tags.join(', ') : '',
          posterUrl: e.posterUrl || '',
          startAt: e.startAt ? toLocalInput(e.startAt) : '',
          endAt: e.endAt ? toLocalInput(e.endAt) : '',
          locationType: e.locationType || 'online',
          venueAddress: e.venueAddress || '',
          onlineUrl: e.onlineUrl || '',
          visibility: e.visibility || 'public',
          registrationMode: e.registrationMode || 'open',
          capacity: e.capacity || '',
          waitlistEnabled: !!e.waitlistEnabled,
          participantListPublic: !!e.participantListPublic,
          waitlistPublic: !!e.waitlistPublic,
          eventType: isCustomType ? '__custom__' : (e.eventType || ''),
          customEventType: isCustomType ? e.eventType : '',
          typeExtras: e.typeExtras || {},
          userExtras: paddedUserExtras,
          termsText: e.termsText || '',
        });
        setOwnerId(e.ownerId || null);
      } catch {
        setError('Event konnte nicht geladen werden');
      }
    })();
  }, [id]);

  // Load possible owners for admin to reassign
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await api.get('/users/list');
        setOwners(res?.data?.items || []);
      } catch {}
    })();
  }, [isAdmin]);

  const descriptionPreview = useMemo(() => {
    try {
      const raw = marked.parse(form?.description || '');
      return { __html: DOMPurify.sanitize(raw) };
    } catch { return { __html: '' }; }
  }, [form?.description]);

  if (!ev || !form) {
    return (
      <div className="home-page">
        <section style={{ width: '100vw', margin: 0, padding: 0 }}>
          <h2 className="hero-title" style={{ textAlign: 'center' }}>Event bearbeiten</h2>
        </section>
        <section style={{ width:'100%', maxWidth:900, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
          {error ? <div style={{ color:'#f87171' }}>{error}</div> : <div>Lädt…</div>}
        </section>
      </div>
    );
  }

  if (!canManage) {
    return <div style={{ padding:24 }}>Keine Berechtigung, dieses Event zu bearbeiten.</div>;
  }

  async function onSubmit(e) {
    e.preventDefault();
    try {
      setSaving(true);
      const finalEventType = form.eventType === '__custom__'
        ? (form.customEventType?.trim() || null)
        : (form.eventType || null);
      // Nur definierte Keys der Schemafelder übernehmen
      const schema = EVENT_TYPE_SCHEMAS[finalEventType] || [];
      const pickedExtras = {};
      for (const def of schema) {
        const v = form.typeExtras?.[def.key];
        if (v != null && String(v).trim() !== '') pickedExtras[def.key] = v;
      }
      // Benutzerdefinierte Felder (bis zu 5)
      const pickedUserExtras = (form.userExtras || [])
        .slice(0,5)
        .map(row => ({ label: (row.label||'').trim(), value: (row.value||'').trim() }))
        .filter(row => row.label !== '' || row.value !== '');
      const payload = {
        title: form.title.trim(),
        description: form.description,
        tags: form.tags ? form.tags.split(',').map(s=>s.trim()).filter(Boolean) : [],
        posterUrl: form.posterUrl || null,
        startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
        endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
        locationType: form.locationType,
        venueAddress: form.locationType === 'venue' ? (form.venueAddress || null) : null,
        onlineUrl: form.locationType === 'online' ? (form.onlineUrl || null) : null,
        visibility: form.visibility,
  registrationMode: form.registrationMode,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        waitlistEnabled: !!form.waitlistEnabled,
        participantListPublic: !!form.participantListPublic,
        waitlistPublic: !!form.waitlistPublic,
        eventType: finalEventType,
        typeExtras: pickedExtras,
        customFields: pickedUserExtras,
        ...(isAdmin && ownerId != null ? { ownerId: parseInt(ownerId, 10) } : {}),
        termsText: form.termsText || null,
      };
      await EventsAPI.update(id, payload);
      navigate(`/events/${id}`);
    } catch {
      alert('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Event bearbeiten</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>{ev.title}</p>
      </section>
      <section style={{ width:'100%', maxWidth:900, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <form onSubmit={onSubmit} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <input required placeholder="Titel" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{ gridColumn:'1/-1' }} />
          <textarea placeholder="Beschreibung (Markdown erlaubt)" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{ gridColumn:'1/-1', minHeight:140 }} />
          <div style={{ gridColumn:'1/-1', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>Vorschau</div>
            {form.description ? (
              <div style={{ color:'#cbd5e1' }} dangerouslySetInnerHTML={descriptionPreview} />
            ) : (
              <div style={{ color:'#9ca3af', fontSize:12 }}>Markdown-Unterstützung: Überschriften, Listen, Links, Bilder.</div>
            )}
          </div>
          <input placeholder="Tags (kommagetrennt)" value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} />
          <input placeholder="Poster-URL (optional)" value={form.posterUrl} onChange={e=>setForm(f=>({...f,posterUrl:e.target.value}))} />
          <label style={{ display:'flex', flexDirection:'column' }}>
            <span style={{ fontSize:13, opacity:0.9 }}>Poster-Datei (optional)</span>
            <input
              type="file"
              accept="image/*"
              onChange={async (e)=>{
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  setUploadingPoster(true);
                  const res = await EventsAPI.uploadPoster(id, file);
                  if (res?.posterUrl) setForm(f=>({ ...f, posterUrl: res.posterUrl }));
                } catch {
                  alert('Poster-Upload fehlgeschlagen');
                } finally {
                  setUploadingPoster(false);
                }
              }}
              disabled={uploadingPoster}
            />
          </label>
          <label>Start <input type="datetime-local" value={form.startAt} onChange={e=>setForm(f=>({...f,startAt:e.target.value}))} required /></label>
          <label>Ende <input type="datetime-local" value={form.endAt} onChange={e=>setForm(f=>({...f,endAt:e.target.value}))} /></label>
          <select value={form.locationType} onChange={e=>setForm(f=>({...f,locationType:e.target.value}))}>
            <option value="online">Online</option>
            <option value="venue">Ort</option>
          </select>
          {form.locationType === 'venue' ? (
            <input placeholder="Adresse" value={form.venueAddress} onChange={e=>setForm(f=>({...f,venueAddress:e.target.value}))} />
          ) : (
            <input placeholder="Online-Link" value={form.onlineUrl} onChange={e=>setForm(f=>({...f,onlineUrl:e.target.value}))} />
          )}
          <select value={form.visibility} onChange={e=>setForm(f=>({...f,visibility:e.target.value}))}>
            <option value="public">öffentlich</option>
            <option value="members">community-only</option>
            <option value="private">privat</option>
          </select>
          <select value={form.registrationMode} onChange={e=>setForm(f=>({...f,registrationMode:e.target.value}))}>
            <option value="open">Anmeldung: frei</option>
            <option value="invite">Anmeldung: nur auf Einladung</option>
            <option value="ticket">Anmeldung: Ticket erforderlich (deaktiviert)</option>
          </select>
          <input placeholder="Kapazität (leer = unbegrenzt)" value={form.capacity} onChange={e=>setForm(f=>({...f,capacity:e.target.value}))} />
          <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={form.waitlistEnabled} onChange={e=>setForm(f=>({...f,waitlistEnabled:e.target.checked}))} /> Warteliste</label>
          <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={form.participantListPublic} onChange={e=>setForm(f=>({...f,participantListPublic:e.target.checked}))} /> Teilnehmerliste öffentlich</label>
          <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={form.waitlistPublic} onChange={e=>setForm(f=>({...f,waitlistPublic:e.target.checked}))} /> Warteliste öffentlich</label>
          <select value={form.eventType} onChange={e=>setForm(f=>({...f,eventType:e.target.value, typeExtras: {}}))}>
            <option value="">– Event-Typ wählen –</option>
            {EVENT_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
            <option value="__custom__">Sonstiges …</option>
          </select>
          {form.eventType === '__custom__' && (
            <input placeholder="Eigener Event-Typ" value={form.customEventType} onChange={e=>setForm(f=>({...f,customEventType:e.target.value}))} />
          )}
          {/* Typabhängige Zusatzfelder */}
          {form.eventType && form.eventType !== '__custom__' && (EVENT_TYPE_SCHEMAS[form.eventType] || []).map(def => (
            <label key={def.key} style={{ display:'flex', flexDirection:'column' }}>
              <span style={{ fontSize:13, opacity:0.9 }}>{def.label}</span>
              <input
                type={def.type === 'url' ? 'url' : 'text'}
                placeholder={def.placeholder || ''}
                value={form.typeExtras?.[def.key] || ''}
                onChange={e=>setForm(f=>({...f, typeExtras: { ...(f.typeExtras||{}), [def.key]: e.target.value }}))}
              />
            </label>
          ))}
          {/* Benutzerdefinierte Zusatzfelder (bis zu 5) */}
          <div style={{ gridColumn:'1/-1', marginTop:8 }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>Zusätzliche Felder (max. 5)</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:8 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <>
                  <input
                    key={`elbl-${i}`}
                    placeholder={`Bezeichnung ${i+1}`}
                    value={form.userExtras?.[i]?.label || ''}
                    onChange={e=>setForm(f=>{
                      const arr = (f.userExtras || []).slice();
                      arr[i] = { ...(arr[i]||{ label:'', value:'' }), label: e.target.value };
                      return { ...f, userExtras: arr };
                    })}
                  />
                  <input
                    key={`eval-${i}`}
                    placeholder={`Wert ${i+1}`}
                    value={form.userExtras?.[i]?.value || ''}
                    onChange={e=>setForm(f=>{
                      const arr = (f.userExtras || []).slice();
                      arr[i] = { ...(arr[i]||{ label:'', value:'' }), value: e.target.value };
                      return { ...f, userExtras: arr };
                    })}
                  />
                </>
              ))}
            </div>
          </div>
          {isAdmin && (
            <label style={{ gridColumn:'1/-1', display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, alignItems:'center', marginTop:8 }}>
              <span style={{ fontSize:13, opacity:0.9 }}>Event-Owner</span>
              <select value={ownerId ?? ''} onChange={e=>setOwnerId(e.target.value ? parseInt(e.target.value,10) : null)}>
                {owners.length === 0 ? <option value="">– lädt –</option> : owners.map(u => (
                  <option key={u.id} value={u.id}>{u.displayName || u.username || u.id}</option>
                ))}
              </select>
            </label>
          )}
          <textarea placeholder="Teilnahmebedingungen (optional)" value={form.termsText} onChange={e=>setForm(f=>({...f,termsText:e.target.value}))} style={{ gridColumn:'1/-1', minHeight:100 }} />
          <div style={{ gridColumn:'1/-1', display:'flex', gap:8 }}>
            <button type="submit" disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
            <button type="button" onClick={()=>navigate(`/events/${id}`)}>Abbrechen</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth()+1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch { return ''; }
}
